/**
 * Auto-sync watcher
 * --------------------------------------------------------------------
 * Whenever the dashboard performs a successful write (POST/PUT/DELETE)
 * against a "relay-relevant" endpoint (templates, components, brands,
 * campaigns, assignments, etc.), we trigger a debounced call to
 * /api/relay/sync to push the latest data to the VPS relay.
 *
 * One global Axios interceptor handles this for every page — no per-page
 * wiring required. The Push to Relay button on RelayControl remains the
 * authoritative manual escape hatch (e.g. when you also need to clear
 * the image cache).
 *
 * Triggered by:
 *   POST/PUT/DELETE /api/templates/...
 *   POST/PUT/DELETE /api/components/...
 *   POST/PUT/DELETE /api/brands/...
 *   POST/PUT/DELETE /api/campaigns/...
 *   POST/PUT/DELETE /api/assignments/...
 *   POST/PUT/DELETE /api/rules/...
 *   POST/PUT/DELETE /api/tenant/...
 *
 * NOT triggered by:
 *   anything under /api/relay/   (avoids infinite loops)
 *   anything under /api/graph/   (read-only sync from M365)
 *   anything under /api/track/   (public click endpoints)
 *   anything under /api/auth/    (auth flows)
 */
import axios from "axios";
import { toast } from "sonner";

const WATCHED_PREFIXES = [
  "/api/templates",
  "/api/components",
  "/api/brands",
  "/api/campaigns",
  "/api/assignments",
  "/api/rules",
  "/api/tenant",
];

const WRITE_METHODS = new Set(["POST", "PUT", "DELETE", "PATCH"]);
const DEBOUNCE_MS = 2000;

let debounceTimer = null;
let lastToastId = null;
let installed = false;  // HMR guard — prevent stacked interceptors

function shouldWatch(config) {
  if (!config) return false;
  const method = (config.method || "").toUpperCase();
  if (!WRITE_METHODS.has(method)) return false;
  const url = config.url || "";
  return WATCHED_PREFIXES.some((p) => url.includes(p));
}

async function performSync() {
  // Show a tiny pill while syncing
  lastToastId = toast.loading("Auto-syncing to relay…", { id: "autosync" });
  try {
    const r = await axios.post("/api/relay/sync", {});
    if (r.data?.ok) {
      const counts = r.data.pushed || {};
      const summary = Object.entries(counts)
        .filter(([, v]) => typeof v === "number" && v > 0)
        .slice(0, 4)
        .map(([k, v]) => `${k}:${v}`)
        .join(" · ");
      toast.success(`Relay synced${summary ? ` → ${summary}` : ""}`, { id: "autosync", duration: 2500 });
    } else {
      toast.error(`Auto-sync failed${r.data?.status_code ? ` (${r.data.status_code})` : ""}`, { id: "autosync" });
    }
  } catch (e) {
    toast.error("Auto-sync failed: " + (e.response?.data?.detail || e.message), { id: "autosync" });
  }
}

export function installAutoSync() {
  // Guard: if App.js is hot-reloaded during development, prevent the interceptor
  // from being stacked multiple times (would cause N parallel syncs per write).
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => {
      try {
        if (shouldWatch(response.config) && response.status >= 200 && response.status < 300) {
          // Coalesce: any further writes within DEBOUNCE_MS reset the timer.
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            debounceTimer = null;
            performSync();
          }, DEBOUNCE_MS);
        }
      } catch {
        // never let interceptor logic break the response chain
      }
      return response;
    },
    (error) => Promise.reject(error)
  );
}

// Expose for manual flush (e.g. used by RelayControl's "Push to Relay" to
// also reset the debounce timer so we don't double-sync immediately after).
// NOTE: this does NOT abort an in-flight sync — only cancels a queued one.
export function cancelPendingAutoSync() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
    if (lastToastId) toast.dismiss(lastToastId);
  }
}
